import {
  v4 as uuid,
} from "uuid";
import querystring from "query-string";
import {
  filter as fuzzyFilter,
} from "fuzzaldrin";

import {
  routes as mainRoutes,
} from "App/routes";
import {
  derived,
} from "CommonState/action-helpers";
import api from "CommonCore/api";
import {
  trimStart,
} from "CommonCore/util";
import apifetch from "CommonCore/apifetch";
import {
  ANTRAGSTELLERMODUS,
} from "App/constants";

import _ from "lodash";

let pluginHilfen = [];

const createSchema = () => {
  // schema dynamisch generieren abhängig vom aktuellen ANTRAGSTELLERMODUS Objekt
  const schema = {
    displayName: "Hilfebeitrag",
    table: [
      {
        field: "hi_master_hiid",
        display: "Übergeordneter Beitrag",
        type: "select",
        required: true,
        inject: ["hilfeList"],
        choices: list => entity => _.filter(list, hi => hi.hi_id !== entity.hi_id)
          .map(hi => ({ value: hi.hi_id, label: hi.hi_titel || "Top-Level" })),
        selector: list => id => _.get(_.find(list, hi => hi.hi_id === id), "hi_titel"),
        default: () => querystring.parse(location.search).master,
        hideFromEditor: entity => entity.hi_id && !entity.hi_master_hiid,
      },
      {
        field: "hi_schlagwoerter",
        display: "Schlagwörter",
        hideFromEditor: entity => entity.hi_id && !entity.hi_master_hiid,
      },
      {
        field: "hi_modus",
        display: "Hilfetext für",
        type: "select",
        control: "selectModus",
        choices: () => [{ value: 0, label: "Allgemein" }].concat(..._.map(ANTRAGSTELLERMODUS, (value, key) => ({ value: key, label: value.label }))),
        selector: () => modus => ANTRAGSTELLERMODUS[modus]?.label,
        default: 0,
        helpText: "Antragsteller sehen den Titel und/oder Text für ihr Ziel, wenn ein individueller Eintrag vorhanden ist. Wenn nicht - und für alle anderen Nutzer - werden die allgemeinen Informationen angezeigt.",
      },
      {
        field: "hi_titel",
        display: "Titel",
        required: true,
        hideFromEditor: entity => entity.hi_id && !entity.hi_master_hiid || entity.hi_modus != 0,
      },
      {
        field: "hi_text",
        display: "Inhalt",
        type: "markdown",
        required: true,
        hideFromEditor: entity => entity.hi_modus != 0,
      },
      {
        field: "hi_hidden",
        display: "Versteckt",
        type: "checkbox",
        default: false,
      },
    ],
  };
  _.forEach(ANTRAGSTELLERMODUS, (value, modus) => {
    schema.table.push({
      field: `hi_titel_${ modus }`,
      display: `${ value.klabel }, Titel`,
      hideFromEditor: entity => entity.hi_id && !entity.hi_master_hiid || entity.hi_modus != modus,
    });
    schema.table.push({
      field: `hi_text_${ modus }`,
      display: `${ value.klabel }, Inhalt`,
      type: "markdown",
      hideFromEditor: entity => entity.hi_modus != modus,
    });
  });

  schema.table.push({
    field: "hi_pos",
    display: "Sortierungsposition",
    type: "number",
    required: true,
    default: 0,
    hideFromEditor: entity => entity.hi_id && !entity.hi_master_hiid,
  });
  schema.table.push({
    field: "hi_pfad",
    display: "Pfad",
    type: "readonly",
    className: "monospace",
    hideFromEditor: entity => entity.hi_id && !entity.hi_master_hiid,
  });

  return schema;
};

const helpApi = api({
  id: "hi_id",
  name: "hilfe",
  schema: createSchema(),
  endpoint: "/api/shkhilfe/hilfe",
  afterRetrieveList: createMissingHelps,
  noUiList: true,
  refresh: {
    hilfe: id => id,
  },
  breadcrumbs: {
    list: "Hilfe",
    detail: hi => hi.hi_titel || "Kein Titel",
  },
});
export default helpApi;

export function searchHelp(help, searchText) {
  const relevantEntries = fuzzyFilter(help, searchText, { key: "hi_search_data" });

  // sort by title, because leaving them sorted by score tends to look haphazard.
  return _.sortBy(relevantEntries, "hi_titel");
}

export function isUrlMatch(location, helpEntry) {
  if (!_.isString(helpEntry.hi_pfad)) {
    return false;
  }
  const template = trimStart(helpEntry.hi_pfad, "/");
  const regex = `^/?${ template.replace("*", ".*?").replace("/", "/+?") }$`;
  return location === helpEntry.hi_pfad || location.match(regex);
}

export function articleToAnchor(hi) {
  const path = hi.hi_pfad;
  return path
? path.split("/").filter(x => x && x !== "*").join("-")
    : hi.hi_id;
}

let helpCache;

export function createMockHelp(routes = mainRoutes, context = [], result = [], masterId) {
  if (helpCache) {
    return helpCache;
  }

  let pos = 0;
  for (const r of routes) {
    const isWildcard = r.path.indexOf(":") !== -1 || r.path.indexOf("*") !== -1;
    context.push(isWildcard ? "*" : r.path.split("/").filter(_.identity).join("/"));

    let childMasterId = masterId;
    if (!isWildcard) {
      // Mock content:
      let content = "";
      content = `<p>${ content.split("\n\n").join("</p><p>") }</p>`;

      childMasterId = uuid();

      result.push({
        hi_id: childMasterId,
        hi_titel: context.map(_.upperFirst).filter(c => c && c !== "*").join(" - "),
        hi_pfad: context.join("/"),
        hi_text: content,
        hi_pos: pos++ * 10,
        hi_master_hiid: masterId,
      });
    }

    if (r.childRoutes) {
      createMockHelp(r.childRoutes, context, result, childMasterId);
    }
    context.pop();
  }

  if (routes === mainRoutes) {
    helpCache = result;
  }
  return result;
}

export const hilfeTree = derived(
  "hilfeTree",
  state => state.hilfeList,
  state => state.user,
  (list, user) => {
    const roots = [];
    const showHidden = (user.funktionen["hilfe.bearbeiten"] || user.funktionen["admin.*"]) !== undefined;
    const childRelations = _.groupBy(_.filter(list, hi => showHidden || !hi.hi_hidden), hi => hi.hi_master_hiid);

    function processNode(node, depth = 0) {
      const children = childRelations[node.data.hi_id];
      _.forEach(children, child => {
        const childNodes = node.children || (node.children = []);
        const childDepth = depth + 1;
        const childNode = {
          data: child,
          depth: childDepth,
          parent: node,
          sectionNode: depth === 1 ? node : node.sectionNode,
        };
        childNodes.push(childNode);
        processNode(childNode, childDepth);
      });
      if (node.children) {
        node.children = _.orderBy(node.children, [
          node => node.data.hi_pos || 0,
          node => node.data.hi_titel,
        ]);
      }
    }

    _.forEach(list, hi => {
      if (!hi.hi_master_hiid) {
        const node = {
          data: hi,
          depth: 0,
        };
        roots.push(node);
        processNode(node);
      }
    });

    return roots;
  },
);


export const searchDataSelector = derived(
  "searchData",
  state => state.hilfeList,
  state => state.user,
  (list, user) => {
    const showHidden = (user.funktionen["hilfe.bearbeiten"] || user.funktionen["admin.*"]) !== undefined;
    return _.map(_.filter(list, hi => showHidden || !hi.hi_hidden), hi => _.assign({}, hi, {
      hi_search_data: `${ hi.hi_titel } ${ hi.hi_schlagwoerter || "" }`,
    }));
  },
);

export const searchResultsSelector = derived(
  "searchResults",
  (state, props) => props.searchData,
  (state, props) => props.searchText,
  (data, searchText) => searchHelp(data, searchText),
);


async function createMissingHelps(helps, state) {
  if (state && state.user && (state.user.funktionen["hilfe.create"] || state.user.funktionen["admin.*"])) {
    // Routes mit Hilfen vergleichen und fehlende Hilfen dem Backend mitteilen
    const missingHelps = getMissingHelpsForRoutes(mainRoutes, helps, "")
      .concat(getMissingHelpsForPlugins(pluginHilfen, helps));
    if (missingHelps.length > 0) {
      const { data: newHelps } = await apifetch(
        `/api/shkhilfe/hilfe/missingbatch`,
        { method: "POST", body: missingHelps },
      );
      return newHelps;
    }

    return helps;
  }

  return helps;
}

function getMissingHelpsForRoutes(routes, helps, basePath) {
  let missingHelps = [];
  routes.forEach(route => {
    // nach ':' in Pfad suchen und alles dahinter mit '*' ersetzen, damit es zu bestehenden Hilfseinträgen passt
    const routePath = route.path.indexOf(":") >= 0
      ? route.path.substring(0, route.path.indexOf(":")) + "*"
      : route.path;
    // parent Pfad an child anhängen, weil Hilfseinträge den vollständigen Pfad haben
    const newBasePath = (basePath + (!basePath.endsWith("/") && !routePath.startsWith("/") ? "/" : "") + routePath)
      .replace("//", "/");

    // Pfad als missing ins Ergebnis einfügen, wenn kein Hilfseintrag dazu gefunden wurde
    if (!_.find(helps, help => isUrlMatch(newBasePath, help))) {
      /*
       * '/*' steht für "not found", wollen wir nicht als Hilfe haben.
       * '.../*' braucht auch keine Hilfseinträge
       */
      if (newBasePath !== "/*" && !newBasePath.endsWith("*")) {
        missingHelps.push(newBasePath);
      }
    }
    // rekursiv durch die childRoutes hangeln
    if (route.childRoutes) {
      missingHelps = missingHelps.concat(getMissingHelpsForRoutes(route.childRoutes, helps, newBasePath));
    }
  });
  return missingHelps;
}

function getMissingHelpsForPlugins(pluginHilfen, helps) {
  const missingHelps = [];
  pluginHilfen.forEach(ph => {
    // Pfad als missing ins Ergebnis einfügen, wenn kein Hilfseintrag dazu gefunden wurde
    if (!_.find(helps, help => help.hi_pfad === ph)) {
      missingHelps.push(ph);
    }
  });
  return missingHelps;
}

export function addPluginHilfen(neuePluginHilfen) {
  pluginHilfen = pluginHilfen.concat(neuePluginHilfen.filter(phNeu => !pluginHilfen.find(ph => ph === phNeu)));
}

